/**
 * @file Damage-over-time for burns left by the flamethrower (issue #14).
 *
 * Owns the lifetime of {@link ../components/Burn.js Burn}: counts it down and
 * routes the damage through the normal `damage` event so shields and
 * invulnerability apply exactly like a bullet. Never mutates health itself —
 * that stays exclusive to {@link ./HealthSystem.js}.
 */

import { System } from '../ecs/System.js';
import { Burn, Health } from '../components/index.js';
import { Events } from '../core/EventBus.js';

export class BurnSystem extends System {
  static priority = 65;

  constructor() {
    super();
    /** @type {object} Reused damage payload. @private */
    this._damageEvent = { target: null, amount: 0, source: null, point: null };
    /** @type {object} Reused expiry payload. @private */
    this._expiredEvent = { entity: null };
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    // Snapshot first: applying damage can destroy entities mid-iteration.
    const burning = this.world.query(Burn, Health);
    for (const entity of burning) {
      if (!entity.alive) continue;

      const burn = entity.get(Burn);
      const health = entity.get(Health);
      if (!burn || !health || health.dead) continue;

      burn.remaining -= dt;
      if (burn.remaining <= 0) {
        entity.remove(Burn);
        this._expiredEvent.entity = entity;
        this.ctx.events.emit(Events.BURN_EXPIRED, this._expiredEvent);
        continue;
      }

      const amount = burn.damagePerSecond * dt;
      if (amount <= 0) continue;

      this._damageEvent.target = entity;
      this._damageEvent.amount = amount;
      this._damageEvent.source = null;
      this.ctx.events.emit(Events.DAMAGE, this._damageEvent);
    }
  }
}
